import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError, API, getToken } from "@/lib/api";
import { CareShell } from "@/components/CareShell";
import { toast } from "sonner";
import {
    Loader2, Plus, Trash2, ShieldCheck, Lock, UserPlus, FileText, X, Download, Pencil,
    HeartPulse, Droplet, Scale, Stethoscope, Pill, Activity, Siren, CalendarClock,
    ChevronRight, Phone, CalendarPlus, HeartHandshake, Clock, ClipboardList,
} from "lucide-react";

const TABS = [
    { key: "condition", label: "Conditions" },
    { key: "medication", label: "Medications" },
    { key: "allergy", label: "Allergies" },
    { key: "emergency_contact", label: "Emergency" },
    { key: "document", label: "Documents" },
];

const AVATAR_COLORS = ["from-[#F43F5E] to-[#FB7185]", "from-[#2563EB] to-[#60A5FA]", "from-[#10B981] to-[#34D399]", "from-[#F59E0B] to-[#FBBF24]", "from-[#8B5CF6] to-[#A78BFA]"];
const colorFor = (name = "") => AVATAR_COLORS[(name.charCodeAt(0) || 0) % AVATAR_COLORS.length];

function Avatar({ name = "?", size = 44 }) {
    return (
        <div className={`flex shrink-0 items-center justify-center rounded-full bg-gradient-to-br ${colorFor(name)} font-heading font-extrabold text-white`} style={{ width: size, height: size, fontSize: size * 0.4 }}>
            {name.trim()[0]?.toUpperCase() || "?"}
        </div>
    );
}

const statusCls = (s) => s === "Normal" ? "bg-emerald-50 text-emerald-600"
    : s === "Stable" || s === "Elevated" ? "bg-amber-50 text-amber-600"
    : s === "High" ? "bg-red-50 text-red-600" : "bg-slate-100 text-slate-500";

const apptIcon = (kind) => ({ doctor: Stethoscope, medicine: Pill, physio: Activity, care_visit: HeartHandshake }[kind] || CalendarClock);

export default function CareHealth() {
    const nav = useNavigate();
    const [profile, setProfile] = useState(null);
    const [memberId, setMemberId] = useState("self");
    const [tab, setTab] = useState("condition");
    const [records, setRecords] = useState(null);
    const [appts, setAppts] = useState(null);
    const [visits, setVisits] = useState([]);
    const [manager, setManager] = useState(null);
    const [sheet, setSheet] = useState(null); // 'record' | 'member' | 'edit' | 'vitals' | 'appt'
    const recordsRef = useRef(null);
    const upcomingRef = useRef(null);

    const loadProfile = useCallback(() => api.get("/care/profile").then((r) => setProfile(r.data)).catch(() => {}), []);
    useEffect(() => { loadProfile(); api.get("/care/manager").then((r) => setManager(r.data)).catch(() => {}); }, [loadProfile]);

    const loadRecords = useCallback(() => {
        if (!profile?.consent) return;
        setRecords(null); setAppts(null);
        api.get(`/care/health-records?member_id=${memberId}`).then((r) => setRecords(r.data)).catch(() => setRecords([]));
        api.get(`/care/appointments?member_id=${memberId}`).then((r) => setAppts(r.data)).catch(() => setAppts([]));
        api.get("/care/visits").then((r) => setVisits(r.data || [])).catch(() => setVisits([]));
    }, [profile, memberId]);
    useEffect(() => { loadRecords(); }, [loadRecords]);

    const giveConsent = async () => {
        try { await api.post("/care/consent", { accepted: true }); toast.success("Consent recorded"); loadProfile(); }
        catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    };

    const callManager = async () => {
        try { await api.post("/care/manager-request"); toast.success("Your Care Manager will reach out shortly"); } catch { /* noop */ }
        if (manager?.phone) window.location.href = `tel:${manager.phone}`;
    };

    const jumpRecords = (t) => { setTab(t); setTimeout(() => recordsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50); };

    if (!profile) return <CareShell><div className="flex min-h-[60vh] items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-[#F43F5E]" /></div></CareShell>;

    if (!profile.consent) {
        return (
            <CareShell>
                <div data-testid="health-consent-gate" className="flex min-h-[60vh] flex-col items-center justify-center px-7 text-center">
                    <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-[#FFF3E8] text-[#F43F5E]"><Lock size={30} /></div>
                    <h1 className="font-heading text-xl font-extrabold text-navy">Your family's health is private</h1>
                    <p className="mt-2 text-sm text-navy/60">HOMIIE Care stores health details only with your explicit consent, to help caregivers support your parents safely. You control this data and can remove it anytime. HOMIIE does not provide medical advice.</p>
                    <button data-testid="give-consent-btn" onClick={giveConsent} className="mt-6 w-full rounded-full bg-[#F43F5E] py-3.5 text-sm font-bold text-white active:scale-95">I AGREE &amp; CONTINUE</button>
                </div>
            </CareShell>
        );
    }

    const current = profile.members.find((m) => m.id === memberId) || profile.members[0];
    const vitals = current?.vitals || {};
    const conditions = current?.conditions || [];
    const filtered = (records || []).filter((r) => r.type === tab);
    const memberVisits = (visits || []).filter((v) => (v.member_id === memberId || (memberId === "self" && !v.member_id)) && v.status !== "CANCELLED" && v.status !== "COMPLETED")
        .map((v) => ({ id: v.id, kind: "care_visit", title: v.plan_name, date: v.scheduled_date || v.date, time: v.scheduled_window || v.window, isVisit: true }));
    const upcoming = [...(appts || []), ...memberVisits].sort((a, b) => String(a.date || "").localeCompare(String(b.date || "")));

    return (
        <CareShell>
            {/* Avatar carousel */}
            <div className="px-5 pt-1">
                <div className="flex items-end gap-3 overflow-x-auto pb-1 homiie-scroll">
                    {profile.members.map((m) => (
                        <button key={m.id} data-testid={`member-${m.id}`} onClick={() => setMemberId(m.id)} className="flex shrink-0 flex-col items-center gap-1">
                            <div className={`rounded-full p-0.5 ${memberId === m.id ? "ring-2 ring-[#F43F5E] ring-offset-2" : ""}`}><Avatar name={m.name} size={54} /></div>
                            <span className={`max-w-[64px] truncate text-[11px] font-bold ${memberId === m.id ? "text-[#F43F5E]" : "text-navy/60"}`}>{m.name.split(" ")[0]}</span>
                            {m.age != null && <span className="text-[9px] text-navy/40">{m.age} yrs</span>}
                        </button>
                    ))}
                    <button data-testid="add-member-btn" onClick={() => setSheet("member")} className="flex shrink-0 flex-col items-center gap-1">
                        <div className="flex h-[54px] w-[54px] items-center justify-center rounded-full border-2 border-dashed border-[#F43F5E]/40 text-[#F43F5E]"><UserPlus size={20} /></div>
                        <span className="text-[11px] font-bold text-[#F43F5E]">Add</span>
                    </button>
                </div>
            </div>

            {/* Profile card */}
            <div className="mt-4 px-5">
                <button data-testid="open-patient-profile" onClick={() => nav(`/care/patients/${current?.id || memberId}`)} className="flex w-full items-center gap-3 rounded-2xl bg-aqua-light/70 p-4 text-left ring-1 ring-teal/10 transition-transform active:scale-[0.98]">
                    <ClipboardList size={22} className="shrink-0 text-teal" />
                    <div className="flex-1"><p className="font-heading text-sm font-bold text-navy">Patient Profile & Health Records</p><p className="text-xs text-navy/55">Lab and diagnostic reports, health timeline and medications{current?.patient_id ? ` · ${current.patient_id}` : ""}</p></div>
                    <ChevronRight size={17} className="text-navy/40" />
                </button>
            </div>
            <div className="mt-3 px-5">
                <div data-testid="care-profile-card" className="rounded-3xl bg-white p-4" style={{ boxShadow: "0 10px 26px -12px rgba(16,42,67,0.14)" }}>
                    <div className="flex items-center gap-3">
                        <Avatar name={current?.name} size={56} />
                        <div className="flex-1">
                            <h1 className="font-heading text-lg font-extrabold leading-tight text-navy">{current?.name}</h1>
                            <p className="text-xs text-navy/55">{[current?.age != null ? `${current.age} yrs` : null, current?.gender, current?.relation].filter(Boolean).join(" · ")}</p>
                        </div>
                        <button data-testid="edit-member-btn" onClick={() => setSheet("edit")} className="flex items-center gap-1 rounded-full bg-[#F43F5E]/10 px-3 py-1.5 text-xs font-bold text-[#F43F5E]"><Pencil size={12} /> Edit</button>
                    </div>
                    {conditions.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-1.5">
                            {conditions.map((c, i) => <span key={i} data-testid={`condition-tag-${i}`} className="rounded-full bg-[#FFF3E8] px-2.5 py-1 text-[11px] font-bold text-[#F43F5E]">{c}</span>)}
                        </div>
                    )}
                </div>
            </div>

            {/* Quick access */}
            <div className="mt-4 grid grid-cols-4 gap-2 px-5">
                <QuickIcon testid="qa-records" icon={FileText} label="Records" onClick={() => jumpRecords("condition")} />
                <QuickIcon testid="qa-meds" icon={Pill} label="Medicines" onClick={() => jumpRecords("medication")} />
                <QuickIcon testid="qa-appts" icon={CalendarClock} label="Appointments" onClick={() => upcomingRef.current?.scrollIntoView({ behavior: "smooth" })} />
                <QuickIcon testid="qa-emergency" icon={Siren} label="Emergency" onClick={() => jumpRecords("emergency_contact")} />
            </div>

            {/* Health overview */}
            <div className="mt-5 px-5">
                <div data-testid="health-overview" className="rounded-3xl bg-white p-4" style={{ boxShadow: "0 10px 26px -12px rgba(16,42,67,0.12)" }}>
                    <div className="mb-3 flex items-center justify-between">
                        <h2 className="font-heading text-base font-extrabold text-navy">Health Overview</h2>
                        <button data-testid="update-vitals-btn" onClick={() => setSheet("vitals")} className="text-xs font-bold text-[#F43F5E]">Update</button>
                    </div>
                    <div className="grid grid-cols-3 gap-2.5">
                        <Vital icon={HeartPulse} label="BP" value={vitals.bp || "—"} status={vitals.bp_status} testid="vital-bp" />
                        <Vital icon={Droplet} label="Sugar" value={vitals.sugar != null ? `${vitals.sugar}` : "—"} status={vitals.sugar_status} testid="vital-sugar" />
                        <Vital icon={Scale} label="Weight" value={vitals.weight != null ? `${vitals.weight} kg` : "—"} status={vitals.weight_status} testid="vital-weight" />
                    </div>
                    {vitals.updated_at && <p className="mt-2.5 text-[10px] text-navy/35">Last updated {new Date(vitals.updated_at).toLocaleDateString()}</p>}
                </div>
            </div>

            {/* Upcoming care */}
            <div ref={upcomingRef} className="mt-5 px-5">
                <div className="mb-2 flex items-center justify-between">
                    <h2 className="font-heading text-base font-extrabold text-navy">Upcoming Care</h2>
                    <button data-testid="add-appt-btn" onClick={() => setSheet("appt")} className="flex items-center gap-0.5 text-xs font-bold text-[#F43F5E]"><Plus size={13} /> Add</button>
                </div>
                {appts === null && <Loader2 className="h-5 w-5 animate-spin text-[#F43F5E]" />}
                {appts !== null && upcoming.length === 0 && <div className="rounded-2xl border border-dashed border-navy/15 py-8 text-center text-sm text-navy/45">No upcoming appointments.</div>}
                <div className="space-y-2.5">
                    {upcoming.map((a) => {
                        const Icon = apptIcon(a.kind);
                        return (
                            <div key={a.id} data-testid={`appt-${a.id}`} className="flex items-center gap-3 rounded-2xl bg-white p-3.5" style={{ boxShadow: "0 8px 20px -10px rgba(16,42,67,0.08)" }}>
                                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#FFF3E8] text-[#F43F5E]"><Icon size={18} /></div>
                                <div className="flex-1">
                                    <p className="font-heading text-sm font-bold text-navy">{a.title}{a.isVisit && <span className="ml-1.5 rounded bg-[#F43F5E]/10 px-1.5 py-0.5 text-[9px] font-bold text-[#F43F5E]">Care visit</span>}</p>
                                    <p className="text-xs text-navy/55">{a.date}{a.time ? ` · ${a.time}` : ""}</p>
                                </div>
                                {!a.isVisit && <button data-testid={`del-appt-${a.id}`} onClick={async () => { await api.delete(`/care/appointments/${a.id}`); loadRecords(); }} className="text-navy/30 hover:text-red-500"><Trash2 size={15} /></button>}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Health records */}
            <div ref={recordsRef} className="mt-6 px-5">
                <h2 className="mb-2 font-heading text-base font-extrabold text-navy">Health Records</h2>
                <div className="flex gap-2 overflow-x-auto pb-1 homiie-scroll">
                    {TABS.map((t) => (
                        <button key={t.key} data-testid={`tab-${t.key}`} onClick={() => setTab(t.key)} className={`shrink-0 rounded-full px-3.5 py-1.5 text-xs font-bold transition-colors ${tab === t.key ? "bg-navy text-white" : "bg-white text-navy/55"}`}>{t.label}</button>
                    ))}
                </div>
                <div className="mt-3 space-y-2.5">
                    {records === null && <Loader2 className="mx-auto mt-4 h-6 w-6 animate-spin text-[#F43F5E]" />}
                    {records !== null && filtered.length === 0 && <div className="rounded-2xl border border-dashed border-navy/15 py-8 text-center text-sm text-navy/45">No {TABS.find((t) => t.key === tab)?.label.toLowerCase()} added yet.</div>}
                    {filtered.map((r) => (
                        <div key={r.id} data-testid={`record-${r.id}`} className="flex items-start gap-3 rounded-2xl bg-white p-4" style={{ boxShadow: "0 8px 20px -10px rgba(16,42,67,0.08)" }}>
                            {r.type === "document" && <FileText size={18} className="mt-0.5 shrink-0 text-[#F43F5E]" />}
                            <div className="flex-1">
                                <h3 className="font-heading text-sm font-bold text-navy">{r.title}</h3>
                                {r.detail && <p className="text-xs text-navy/55">{r.detail}</p>}
                                {r.doc_path && <a href={`${API}/files/${r.doc_path}?auth=${getToken()}`} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-xs font-bold text-[#F43F5E]"><Download size={12} /> {r.doc_name || "View document"}</a>}
                            </div>
                            <button data-testid={`del-record-${r.id}`} onClick={async () => { await api.delete(`/care/health-records/${r.id}`); loadRecords(); }} className="text-navy/30 hover:text-red-500"><Trash2 size={16} /></button>
                        </div>
                    ))}
                </div>
                <button data-testid="add-record-btn" onClick={() => setSheet("record")} className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-full border border-[#F43F5E]/30 py-2.5 text-sm font-bold text-[#F43F5E] active:scale-95"><Plus size={16} /> ADD {TABS.find((t) => t.key === tab)?.label.toUpperCase()}</button>
            </div>

            {/* Actions */}
            <div className="mt-6 flex gap-3 px-5 pb-4">
                <button data-testid="care-book-visit-btn" onClick={() => nav("/care/plans")} className="flex flex-1 items-center justify-center gap-2 rounded-full bg-[#F43F5E] py-3.5 text-sm font-bold text-white active:scale-95"><CalendarPlus size={16} /> Book a Care Visit</button>
                <button data-testid="care-manager-btn" onClick={callManager} className="flex flex-1 items-center justify-center gap-2 rounded-full border border-navy/15 bg-white py-3.5 text-sm font-bold text-navy active:scale-95"><Phone size={16} /> Care Manager</button>
            </div>

            {sheet === "record" && <AddRecord memberId={memberId} type={tab} onClose={() => setSheet(null)} onDone={() => { setSheet(null); loadRecords(); }} />}
            {sheet === "member" && <MemberForm onClose={() => setSheet(null)} onDone={() => { setSheet(null); loadProfile(); }} />}
            {sheet === "edit" && <MemberForm member={current} onClose={() => setSheet(null)} onDone={() => { setSheet(null); loadProfile(); }} />}
            {sheet === "vitals" && <VitalsForm memberId={memberId} vitals={vitals} onClose={() => setSheet(null)} onDone={() => { setSheet(null); loadProfile(); }} />}
            {sheet === "appt" && <AppointmentForm memberId={memberId} onClose={() => setSheet(null)} onDone={() => { setSheet(null); loadRecords(); }} />}
        </CareShell>
    );
}

function QuickIcon({ icon: Icon, label, onClick, testid }) {
    return (
        <button data-testid={testid} onClick={onClick} className="flex flex-col items-center gap-1.5 rounded-2xl bg-white p-3 active:scale-95" style={{ boxShadow: "0 6px 16px -10px rgba(16,42,67,0.12)" }}>
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#FFF3E8] text-[#F43F5E]"><Icon size={17} /></span>
            <span className="text-[10px] font-bold text-navy/70">{label}</span>
        </button>
    );
}

function Vital({ icon: Icon, label, value, status, testid }) {
    return (
        <div data-testid={testid} className="rounded-2xl bg-[#FFFAF5] p-3 text-center">
            <Icon size={16} className="mx-auto text-[#F43F5E]" />
            <p className="mt-1 text-[10px] font-semibold uppercase tracking-wide text-navy/40">{label}</p>
            <p className="font-heading text-base font-extrabold text-navy">{value}</p>
            {status && <span className={`mt-1 inline-block rounded-full px-2 py-0.5 text-[9px] font-bold ${statusCls(status)}`}>{status}</span>}
        </div>
    );
}

function Sheet({ children, onClose }) {
    return (
        <div className="fixed inset-0 z-[70] flex items-end justify-center bg-navy/50 backdrop-blur-sm" onClick={onClose}>
            <div className="mx-auto max-h-[85vh] w-full max-w-md animate-fade-up overflow-y-auto rounded-t-3xl bg-[#FFFAF5] p-6 pb-8 homiie-scroll" onClick={(e) => e.stopPropagation()}>{children}</div>
        </div>
    );
}

const inputCls = "w-full rounded-xl border border-navy/15 bg-white px-3 py-3 text-sm text-navy outline-none focus:border-[#F43F5E]";

function MemberForm({ member, onClose, onDone }) {
    const editing = !!member && member.id !== undefined;
    const [f, setF] = useState({ name: member?.name || "", relation: member?.relation || "", age: member?.age ?? "", gender: member?.gender || "", conditions: (member?.conditions || []).join(", ") });
    const [busy, setBusy] = useState(false);
    const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
    const save = async () => {
        if (!f.name.trim()) return toast.error("Please add a name");
        setBusy(true);
        const body = { name: f.name.trim(), relation: f.relation || "Family", age: f.age === "" ? null : Number(f.age), gender: f.gender || null, conditions: f.conditions.split(",").map((x) => x.trim()).filter(Boolean) };
        try {
            if (editing) await api.patch(`/care/members/${member.id}`, body);
            else await api.post("/care/members", body);
            toast.success(editing ? "Profile updated" : "Family member added"); onDone();
        } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
        finally { setBusy(false); }
    };
    return (
        <Sheet onClose={onClose}>
            <div className="mb-3 flex items-center justify-between"><h3 className="font-heading text-lg font-extrabold text-navy">{editing ? "Edit profile" : "Add family member"}</h3><button onClick={onClose} className="rounded-full bg-navy/5 p-1.5"><X size={16} /></button></div>
            <div className="space-y-2.5">
                <input data-testid="member-name" value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="Full name (e.g. Papaji)" className={inputCls} />
                <input data-testid="member-relation" value={f.relation} onChange={(e) => set("relation", e.target.value)} placeholder="Relation (e.g. Father, Mother)" className={inputCls} />
                <div className="flex gap-2.5">
                    <input data-testid="member-age" type="number" value={f.age} onChange={(e) => set("age", e.target.value)} placeholder="Age" className={inputCls} />
                    <select data-testid="member-gender" value={f.gender} onChange={(e) => set("gender", e.target.value)} className={inputCls}>
                        <option value="">Gender</option><option>Male</option><option>Female</option><option>Other</option>
                    </select>
                </div>
                <input data-testid="member-conditions" value={f.conditions} onChange={(e) => set("conditions", e.target.value)} placeholder="Conditions, comma separated (e.g. Hypertension, Diabetes)" className={inputCls} />
            </div>
            <button data-testid="save-member-btn" disabled={busy} onClick={save} className="mt-4 w-full rounded-full bg-[#F43F5E] py-3.5 text-sm font-bold text-white disabled:opacity-60">{busy && <Loader2 className="mr-1 inline h-4 w-4 animate-spin" />}{editing ? "SAVE CHANGES" : "ADD MEMBER"}</button>
        </Sheet>
    );
}

function VitalsForm({ memberId, vitals, onClose, onDone }) {
    const [f, setF] = useState({ bp: vitals.bp || "", sugar: vitals.sugar ?? "", weight: vitals.weight ?? "" });
    const [busy, setBusy] = useState(false);
    const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
    const save = async () => {
        setBusy(true);
        const body = { bp: f.bp || null, sugar: f.sugar === "" ? null : Number(f.sugar), weight: f.weight === "" ? null : Number(f.weight) };
        try { await api.post(`/care/members/${memberId}/vitals`, body); toast.success("Vitals updated"); onDone(); }
        catch (err) { toast.error(apiError(err.response?.data?.detail)); }
        finally { setBusy(false); }
    };
    return (
        <Sheet onClose={onClose}>
            <div className="mb-3 flex items-center justify-between"><h3 className="font-heading text-lg font-extrabold text-navy">Update vitals</h3><button onClick={onClose} className="rounded-full bg-navy/5 p-1.5"><X size={16} /></button></div>
            <div className="space-y-2.5">
                <div><label className="mb-1 block text-xs font-semibold text-navy/60">Blood pressure (e.g. 124/82)</label><input data-testid="vitals-bp" value={f.bp} onChange={(e) => set("bp", e.target.value)} placeholder="124/82" className={inputCls} /></div>
                <div><label className="mb-1 block text-xs font-semibold text-navy/60">Blood sugar (mg/dL)</label><input data-testid="vitals-sugar" type="number" value={f.sugar} onChange={(e) => set("sugar", e.target.value)} placeholder="132" className={inputCls} /></div>
                <div><label className="mb-1 block text-xs font-semibold text-navy/60">Weight (kg)</label><input data-testid="vitals-weight" type="number" value={f.weight} onChange={(e) => set("weight", e.target.value)} placeholder="66" className={inputCls} /></div>
            </div>
            <button data-testid="save-vitals-btn" disabled={busy} onClick={save} className="mt-4 w-full rounded-full bg-[#F43F5E] py-3.5 text-sm font-bold text-white disabled:opacity-60">{busy && <Loader2 className="mr-1 inline h-4 w-4 animate-spin" />}SAVE VITALS</button>
        </Sheet>
    );
}

function AppointmentForm({ memberId, onClose, onDone }) {
    const [f, setF] = useState({ kind: "doctor", title: "", date: "", time: "" });
    const [busy, setBusy] = useState(false);
    const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
    const save = async () => {
        if (!f.title.trim() || !f.date) return toast.error("Add a title and date");
        setBusy(true);
        try { await api.post("/care/appointments", { member_id: memberId, kind: f.kind, title: f.title.trim(), date: f.date, time: f.time || null }); toast.success("Appointment added"); onDone(); }
        catch (err) { toast.error(apiError(err.response?.data?.detail)); }
        finally { setBusy(false); }
    };
    return (
        <Sheet onClose={onClose}>
            <div className="mb-3 flex items-center justify-between"><h3 className="font-heading text-lg font-extrabold text-navy">Add appointment</h3><button onClick={onClose} className="rounded-full bg-navy/5 p-1.5"><X size={16} /></button></div>
            <div className="space-y-2.5">
                <select data-testid="appt-kind" value={f.kind} onChange={(e) => set("kind", e.target.value)} className={inputCls}>
                    <option value="doctor">Doctor Visit</option><option value="medicine">Medicine Reminder</option><option value="physio">Physiotherapy</option><option value="other">Other</option>
                </select>
                <input data-testid="appt-title" value={f.title} onChange={(e) => set("title", e.target.value)} placeholder="Title (e.g. Cardiologist check-up)" className={inputCls} />
                <div className="flex gap-2.5">
                    <input data-testid="appt-date" type="date" value={f.date} onChange={(e) => set("date", e.target.value)} className={inputCls} />
                    <input data-testid="appt-time" value={f.time} onChange={(e) => set("time", e.target.value)} placeholder="10:00 AM" className={inputCls} />
                </div>
            </div>
            <button data-testid="save-appt-btn" disabled={busy} onClick={save} className="mt-4 w-full rounded-full bg-[#F43F5E] py-3.5 text-sm font-bold text-white disabled:opacity-60">{busy && <Loader2 className="mr-1 inline h-4 w-4 animate-spin" />}ADD APPOINTMENT</button>
        </Sheet>
    );
}

function AddRecord({ memberId, type, onClose, onDone }) {
    const [title, setTitle] = useState("");
    const [detail, setDetail] = useState("");
    const [doc, setDoc] = useState(null);
    const [busy, setBusy] = useState(false);
    const label = TABS.find((t) => t.key === type)?.label;

    const upload = async (e) => {
        const file = e.target.files?.[0]; if (!file) return;
        const fd = new FormData(); fd.append("file", file);
        try { const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); setDoc({ path: data.path, name: file.name }); toast.success("File attached"); }
        catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    };

    const save = async () => {
        if (!title.trim()) return toast.error("Please add a title");
        setBusy(true);
        try {
            await api.post("/care/health-records", { member_id: memberId, type, title, detail: detail || null, doc_path: doc?.path, doc_name: doc?.name });
            toast.success("Saved"); onDone();
        } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    return (
        <Sheet onClose={onClose}>
            <div className="mb-3 flex items-center justify-between"><h3 className="font-heading text-lg font-extrabold text-navy">Add {label}</h3><button onClick={onClose} className="rounded-full bg-navy/5 p-1.5"><X size={16} /></button></div>
            <input data-testid="record-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={type === "emergency_contact" ? "Name" : type === "medication" ? "Medicine name & dose" : "Title"} className={inputCls} />
            <input data-testid="record-detail" value={detail} onChange={(e) => setDetail(e.target.value)} placeholder={type === "emergency_contact" ? "Phone / relation" : "Notes (optional)"} className={`mt-2 ${inputCls}`} />
            {type === "document" && (
                <label data-testid="record-doc-upload" className="mt-2 flex cursor-pointer items-center justify-center gap-1.5 rounded-xl border border-dashed border-navy/25 py-3 text-sm font-semibold text-navy/60">
                    <FileText size={16} /> {doc ? doc.name : "Attach report / document"}
                    <input type="file" accept="image/*,application/pdf" className="hidden" onChange={upload} />
                </label>
            )}
            <button data-testid="save-record-btn" disabled={busy} onClick={save} className="mt-4 w-full rounded-full bg-[#F43F5E] py-3.5 text-sm font-bold text-white disabled:opacity-60">{busy && <Loader2 className="mr-1 inline h-4 w-4 animate-spin" />}SAVE</button>
        </Sheet>
    );
}
